import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  LogOut,
  Search,
  Loader2,
  Save,
  Star,
  MessageCircle,
  UtensilsCrossed,
  ClipboardList,
  Calendar,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "starters", label: "Starters" },
  { key: "main_course", label: "Main Course" },
  { key: "rice_bread", label: "Rice & Bread" },
  { key: "desserts", label: "Desserts" },
  { key: "salads", label: "Salads" },
  { key: "indo_chinese", label: "Indo-Chinese" },
  { key: "raita", label: "Raita" },
];

const EMPTY_ITEM = {
  name: "",
  description: "",
  category: "starters",
  is_veg: true,
  pricing_type: "tray",
  prices: { small: 0, medium: 0, med_large: 0, large: 0 },
};

function getAuthHeaders() {
  const token = sessionStorage.getItem("admin_token");
  return { Authorization: `Bearer ${token}` };
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("menu");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add"); // add | edit
  const [editForm, setEditForm] = useState({ ...EMPTY_ITEM });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [respondOpen, setRespondOpen] = useState(false);
  const [respondReview, setRespondReview] = useState(null);
  const [respondText, setRespondText] = useState("");
  const [responding, setResponding] = useState(false);
  const [deleteReviewOpen, setDeleteReviewOpen] = useState(false);
  const [deleteReviewItem, setDeleteReviewItem] = useState(null);
  const [deletingReview, setDeletingReview] = useState(false);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderDateFilter, setOrderDateFilter] = useState("all");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [deleteOrderOpen, setDeleteOrderOpen] = useState(false);
  const [deleteOrderItem, setDeleteOrderItem] = useState(null);
  const [changePassOpen, setChangePassOpen] = useState(false);
  const [passForm, setPassForm] = useState({ current: "", new: "", confirm: "" });
  const [passLoading, setPassLoading] = useState(false);

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, { headers: getAuthHeaders() });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      toast.success(`Order marked as ${newStatus}`);
    } catch (err) {
      toast.error("Failed to update order status");
    }
  };

  const handleDeleteOrder = async () => {
    if (!deleteOrderItem) return;
    try {
      await axios.delete(`${API}/admin/orders/${deleteOrderItem.id}`, { headers: getAuthHeaders() });
      setOrders(prev => prev.filter(o => o.id !== deleteOrderItem.id));
      toast.success("Order deleted");
      setDeleteOrderOpen(false);
      setDeleteOrderItem(null);
    } catch (err) {
      toast.error("Failed to delete order");
    }
  };

  const handleChangePassword = async () => {
    if (passForm.new !== passForm.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (passForm.new.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setPassLoading(true);
    try {
      await axios.put(`${API}/admin/change-password`, {
        current_password: passForm.current,
        new_password: passForm.new,
      }, { headers: getAuthHeaders() });
      toast.success("Password changed successfully");
      setChangePassOpen(false);
      setPassForm({ current: "", new: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setPassLoading(false);
    }
  };

  const fetchMenu = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/menu`);
      setItems(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Verify admin token
    const token = sessionStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    axios
      .get(`${API}/admin/verify`, { headers: getAuthHeaders() })
      .then(() => fetchMenu())
      .catch(() => {
        sessionStorage.removeItem("admin_token");
        navigate("/admin");
      });
  }, [navigate, fetchMenu]);

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    navigate("/admin");
    toast.info("Logged out");
  };

  // Open add dialog
  const openAdd = () => {
    setDialogMode("add");
    setEditForm({ ...EMPTY_ITEM });
    setEditId(null);
    setDialogOpen(true);
  };

  // Open edit dialog
  const openEdit = (item) => {
    setDialogMode("edit");
    setEditForm({
      name: item.name,
      description: item.description,
      category: item.category,
      is_veg: item.is_veg,
      pricing_type: item.pricing_type || "tray",
      prices: { small: item.prices?.small || 0, medium: item.prices?.medium || 0, med_large: item.prices?.med_large || 0, large: item.prices?.large || 0 },
    });
    setEditId(item.id);
    setDialogOpen(true);
  };

  // Save (add or edit)
  const handleSave = async () => {
    if (!editForm.name || !editForm.category) {
      toast.error("Name and category are required");
      return;
    }
    setSaving(true);
    try {
      if (dialogMode === "add") {
        await axios.post(`${API}/admin/menu`, editForm, {
          headers: getAuthHeaders(),
        });
        toast.success(`"${editForm.name}" added to menu`);
      } else {
        await axios.put(`${API}/admin/menu/${editId}`, editForm, {
          headers: getAuthHeaders(),
        });
        toast.success(`"${editForm.name}" updated`);
      }
      setDialogOpen(false);
      fetchMenu();
    } catch (err) {
      toast.error("Failed to save item");
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/menu/${deleteItem.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success(`"${deleteItem.name}" removed from menu`);
      setDeleteOpen(false);
      setDeleteItem(null);
      fetchMenu();
    } catch (err) {
      toast.error("Failed to delete item");
    } finally {
      setDeleting(false);
    }
  };

  // --- Reviews Management ---
  const fetchReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reviews`, { headers: getAuthHeaders() });
      setReviews(res.data);
    } catch (err) {
      console.error("Failed to load reviews:", err);
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers: getAuthHeaders() });
      setOrders(res.data);
    } catch (err) {
      console.error("Failed to load orders:", err);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "reviews" && reviews.length === 0) {
      fetchReviews();
    }
    if (activeTab === "orders" && orders.length === 0) {
      fetchOrders();
    }
  }, [activeTab, reviews.length, fetchReviews, orders.length, fetchOrders]);

  const handleRespond = async () => {
    if (!respondReview || !respondText.trim()) return;
    setResponding(true);
    try {
      await axios.put(
        `${API}/admin/reviews/${respondReview.id}/respond`,
        { response: respondText },
        { headers: getAuthHeaders() }
      );
      toast.success("Response posted");
      setRespondOpen(false);
      setRespondReview(null);
      setRespondText("");
      fetchReviews();
    } catch (err) {
      toast.error("Failed to post response");
    } finally {
      setResponding(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!deleteReviewItem) return;
    setDeletingReview(true);
    try {
      await axios.delete(`${API}/admin/reviews/${deleteReviewItem.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Review deleted");
      setDeleteReviewOpen(false);
      setDeleteReviewItem(null);
      fetchReviews();
    } catch (err) {
      toast.error("Failed to delete review");
    } finally {
      setDeletingReview(false);
    }
  };

  const filtered = items.filter((item) => {
    if (filterCat !== "all" && item.category !== filterCat) return false;
    if (search && !item.name.toLowerCase().includes(search.toLowerCase()))
      return false;
    return true;
  });

  const catLabel = (key) =>
    CATEGORIES.find((c) => c.key === key)?.label || key;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#EA580C]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-[#78350F] py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-body text-xs text-[#FED7AA] tracking-[0.15em] uppercase font-semibold">
                Admin Dashboard
              </p>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
                {activeTab === "menu" ? "Menu Management" : activeTab === "reviews" ? "Reviews Management" : "Order History"}
              </h1>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="admin-change-password-btn"
                onClick={() => setChangePassOpen(true)}
                variant="outline"
                className="rounded-full border-white/30 text-white hover:bg-white/10 font-body text-sm"
              >
                <KeyRound className="h-4 w-4 mr-2" /> Change Password
              </Button>
              <Button
                data-testid="admin-logout-btn"
                onClick={handleLogout}
                variant="outline"
                className="rounded-full border-white/30 text-white hover:bg-white/10 font-body text-sm"
              >
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="admin-tab-menu"
              onClick={() => setActiveTab("menu")}
              className={`px-5 py-2 rounded-full text-sm font-body font-medium transition-all ${
                activeTab === "menu"
                  ? "bg-white text-[#78350F]"
                  : "bg-white/10 text-white/80 hover:bg-white/20"
              }`}
            >
              <UtensilsCrossed className="h-4 w-4 inline mr-1.5 -mt-0.5" />
              Menu
            </button>
            <button
              data-testid="admin-tab-reviews"
              onClick={() => setActiveTab("reviews")}
              className={`px-5 py-2 rounded-full text-sm font-body font-medium transition-all ${
                activeTab === "reviews"
                  ? "bg-white text-[#78350F]"
                  : "bg-white/10 text-white/80 hover:bg-white/20"
              }`}
            >
              <Star className="h-4 w-4 inline mr-1.5 -mt-0.5" />
              Reviews {reviews.length > 0 && `(${reviews.length})`}
            </button>
            <button
              data-testid="admin-tab-orders"
              onClick={() => setActiveTab("orders")}
              className={`px-5 py-2 rounded-full text-sm font-body font-medium transition-all ${
                activeTab === "orders"
                  ? "bg-white text-[#78350F]"
                  : "bg-white/10 text-white/80 hover:bg-white/20"
              }`}
            >
              <ClipboardList className="h-4 w-4 inline mr-1.5 -mt-0.5" />
              Orders {orders.length > 0 && `(${orders.length})`}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === "menu" && (
        <>
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                data-testid="admin-search"
                placeholder="Search items..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white border-stone-200 rounded-xl h-10 font-body"
              />
            </div>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger
                data-testid="admin-filter-category"
                className="w-full sm:w-44 bg-white border-stone-200 rounded-xl h-10 font-body"
              >
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="font-body">
                  All Categories
                </SelectItem>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.key} value={c.key} className="font-body">
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid="admin-add-item-btn"
            onClick={openAdd}
            className="rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold px-6 h-10 shadow-md transition-all active:scale-95"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Item
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-stone-100 p-4">
            <p className="font-body text-xs text-stone-400 uppercase tracking-wider">
              Total Items
            </p>
            <p
              data-testid="admin-total-items"
              className="font-heading text-2xl font-bold text-[#78350F]"
            >
              {items.length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-stone-100 p-4">
            <p className="font-body text-xs text-stone-400 uppercase tracking-wider">
              Veg Items
            </p>
            <p className="font-heading text-2xl font-bold text-green-600">
              {items.filter((i) => i.is_veg).length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-stone-100 p-4">
            <p className="font-body text-xs text-stone-400 uppercase tracking-wider">
              Non-Veg
            </p>
            <p className="font-heading text-2xl font-bold text-red-600">
              {items.filter((i) => !i.is_veg).length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-stone-100 p-4">
            <p className="font-body text-xs text-stone-400 uppercase tracking-wider">
              Categories
            </p>
            <p className="font-heading text-2xl font-bold text-[#EA580C]">
              {new Set(items.map((i) => i.category)).size}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_12px_rgb(0,0,0,0.03)] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50/80">
                <TableHead className="font-body text-xs font-semibold text-stone-500 uppercase tracking-wider w-8">
                  Type
                </TableHead>
                <TableHead className="font-body text-xs font-semibold text-stone-500 uppercase tracking-wider">
                  Name
                </TableHead>
                <TableHead className="font-body text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">
                  Category
                </TableHead>
                <TableHead className="font-body text-xs font-semibold text-stone-500 uppercase tracking-wider text-center">
                  Small
                </TableHead>
                <TableHead className="font-body text-xs font-semibold text-stone-500 uppercase tracking-wider text-center">
                  Medium
                </TableHead>
                <TableHead className="font-body text-xs font-semibold text-stone-500 uppercase tracking-wider text-center">
                  Med-Lg
                </TableHead>
                <TableHead className="font-body text-xs font-semibold text-stone-500 uppercase tracking-wider text-center">
                  Large
                </TableHead>
                <TableHead className="font-body text-xs font-semibold text-stone-500 uppercase tracking-wider text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-12 font-body text-stone-400"
                  >
                    No items found
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((item) => (
                  <TableRow key={item.id} data-testid={`admin-row-${item.id}`}>
                    <TableCell>
                      <span
                        className={`inline-flex items-center justify-center w-4 h-4 rounded-sm border-2 ${
                          item.is_veg
                            ? "border-green-600"
                            : "border-red-600"
                        }`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full ${
                            item.is_veg ? "bg-green-600" : "bg-red-600"
                          }`}
                        />
                      </span>
                    </TableCell>
                    <TableCell>
                      <p className="font-body font-semibold text-sm text-[#78350F]">
                        {item.name}
                      </p>
                      <p className="font-body text-xs text-stone-400 line-clamp-1 max-w-xs">
                        {item.description}
                      </p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge
                        variant="outline"
                        className="font-body text-xs text-stone-500 border-stone-200"
                      >
                        {catLabel(item.category)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center font-body text-sm font-medium">
                      ${item.prices?.small || 0}
                    </TableCell>
                    <TableCell className="text-center font-body text-sm font-medium">
                      ${item.prices?.medium || 0}
                    </TableCell>
                    <TableCell className="text-center font-body text-sm font-medium">
                      ${item.prices?.med_large || 0}
                    </TableCell>
                    <TableCell className="text-center font-body text-sm font-medium">
                      ${item.prices?.large || 0}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`admin-edit-${item.id}`}
                          onClick={() => openEdit(item)}
                          className="p-2 rounded-lg hover:bg-amber-50 text-stone-400 hover:text-[#EA580C] transition-colors"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`admin-delete-${item.id}`}
                          onClick={() => {
                            setDeleteItem(item);
                            setDeleteOpen(true);
                          }}
                          className="p-2 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <p className="font-body text-xs text-stone-400 text-center mt-4">
          Showing {filtered.length} of {items.length} items
        </p>
        </>
        )}
        {activeTab === "reviews" && (
        /* Reviews Tab */
        <>
          {reviewsLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-[#EA580C]" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-16">
              <Star className="h-10 w-10 text-stone-200 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-[#78350F] mb-2">No reviews yet</h3>
              <p className="font-body text-sm text-stone-500">Customer reviews will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                <div className="bg-white rounded-xl border border-stone-100 p-4">
                  <p className="font-body text-xs text-stone-400 uppercase tracking-wider">Total Reviews</p>
                  <p className="font-heading text-2xl font-bold text-[#78350F]">{reviews.length}</p>
                </div>
                <div className="bg-white rounded-xl border border-stone-100 p-4">
                  <p className="font-body text-xs text-stone-400 uppercase tracking-wider">Avg Rating</p>
                  <p className="font-heading text-2xl font-bold text-amber-500">
                    {(reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)}
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-stone-100 p-4">
                  <p className="font-body text-xs text-stone-400 uppercase tracking-wider">Responded</p>
                  <p className="font-heading text-2xl font-bold text-green-600">
                    {reviews.filter((r) => r.admin_response).length}
                  </p>
                </div>
              </div>
              {reviews.map((review) => (
                <div
                  key={review.id}
                  data-testid={`admin-review-${review.id}`}
                  className="bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_2px_12px_rgb(0,0,0,0.03)]"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-body font-semibold text-[#78350F]">{review.name}</h4>
                      <p className="font-body text-xs text-stone-400 mt-0.5">
                        {new Date(review.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`h-4 w-4 ${s <= review.rating ? "fill-amber-400 text-amber-400" : "fill-none text-stone-300"}`} />
                        ))}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`admin-respond-${review.id}`}
                          onClick={() => { setRespondReview(review); setRespondText(review.admin_response || ""); setRespondOpen(true); }}
                          className="p-2 rounded-lg hover:bg-amber-50 text-stone-400 hover:text-[#EA580C] transition-colors"
                          title={review.admin_response ? "Edit response" : "Respond"}
                        >
                          <MessageCircle className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`admin-delete-review-${review.id}`}
                          onClick={() => { setDeleteReviewItem(review); setDeleteReviewOpen(true); }}
                          className="p-2 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="font-body text-sm text-stone-600 leading-relaxed">{review.comment}</p>
                  {review.admin_response && (
                    <div className="mt-4 bg-amber-50 rounded-xl p-4 border border-amber-100">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageCircle className="h-3.5 w-3.5 text-[#EA580C]" />
                        <span className="font-body text-xs font-semibold text-[#EA580C] uppercase tracking-wider">Your Response</span>
                      </div>
                      <p className="font-body text-sm text-stone-600">{review.admin_response}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
        )}
        {activeTab === "orders" && (
        <>
          {ordersLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-[#EA580C]" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Order Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-white rounded-xl border border-stone-100 p-4">
                  <p className="font-body text-xs text-stone-400 uppercase tracking-wider">Total Orders</p>
                  <p data-testid="orders-total-count" className="font-heading text-2xl font-bold text-[#78350F]">{orders.length}</p>
                </div>
                <div className="bg-white rounded-xl border border-stone-100 p-4">
                  <p className="font-body text-xs text-stone-400 uppercase tracking-wider">Total Revenue</p>
                  <p data-testid="orders-total-revenue" className="font-heading text-2xl font-bold text-green-600">
                    ${orders.reduce((s, o) => s + (o.subtotal || 0), 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-stone-100 p-4">
                  <p className="font-body text-xs text-stone-400 uppercase tracking-wider">Pending</p>
                  <p className="font-heading text-2xl font-bold text-amber-500">{orders.filter(o => o.status === "pending").length}</p>
                </div>
                <div className="bg-white rounded-xl border border-stone-100 p-4">
                  <p className="font-body text-xs text-stone-400 uppercase tracking-wider">This Month</p>
                  <p className="font-heading text-2xl font-bold text-[#EA580C]">
                    {orders.filter(o => {
                      const d = new Date(o.created_at);
                      const now = new Date();
                      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                    }).length}
                  </p>
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    data-testid="order-search"
                    placeholder="Search by name, email, phone..."
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                    className="pl-10 bg-white border-stone-200 rounded-xl h-10 font-body"
                  />
                </div>
                <Select value={orderDateFilter} onValueChange={setOrderDateFilter}>
                  <SelectTrigger data-testid="order-date-filter" className="w-full sm:w-48 bg-white border-stone-200 rounded-xl h-10 font-body">
                    <Calendar className="h-4 w-4 mr-2 text-stone-400" />
                    <SelectValue placeholder="Filter by date" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">This Week</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  data-testid="orders-refresh-btn"
                  onClick={fetchOrders}
                  variant="outline"
                  className="rounded-xl font-body text-sm border-stone-200"
                >
                  Refresh
                </Button>
              </div>

              {/* Orders List */}
              {(() => {
                const filtered = orders.filter(o => {
                  const cust = o.customer || {};
                  const q = orderSearch.toLowerCase();
                  const matchesSearch = !q || 
                    (cust.name || "").toLowerCase().includes(q) ||
                    (cust.email || "").toLowerCase().includes(q) ||
                    (cust.phone || "").toLowerCase().includes(q) ||
                    (o.id || "").toLowerCase().includes(q);

                  let matchesDate = true;
                  if (orderDateFilter !== "all") {
                    const orderDate = new Date(o.created_at);
                    const now = new Date();
                    if (orderDateFilter === "today") {
                      matchesDate = orderDate.toDateString() === now.toDateString();
                    } else if (orderDateFilter === "week") {
                      const weekAgo = new Date(now);
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      matchesDate = orderDate >= weekAgo;
                    } else if (orderDateFilter === "month") {
                      matchesDate = orderDate.getMonth() === now.getMonth() && orderDate.getFullYear() === now.getFullYear();
                    }
                  }
                  return matchesSearch && matchesDate;
                });

                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-16">
                      <ClipboardList className="h-10 w-10 text-stone-200 mx-auto mb-4" />
                      <h3 className="font-heading text-xl text-[#78350F] mb-2">No orders found</h3>
                      <p className="font-body text-sm text-stone-500">
                        {orders.length === 0 ? "Orders will appear here once customers place them" : "Try adjusting your search or filters"}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {filtered.map((order) => {
                      const cust = order.customer || {};
                      const isExpanded = expandedOrder === order.id;
                      const orderDate = new Date(order.created_at);
                      return (
                        <div
                          key={order.id}
                          data-testid={`order-card-${order.id}`}
                          className="bg-white rounded-xl border border-stone-100 overflow-hidden transition-shadow hover:shadow-md"
                        >
                          {/* Order Header - Always visible */}
                          <button
                            data-testid={`order-toggle-${order.id}`}
                            onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                            className="w-full px-4 py-3 flex items-center justify-between text-left"
                          >
                            <div className="flex items-center gap-4 flex-wrap">
                              <div>
                                <p className="font-heading text-sm font-bold text-[#78350F]">
                                  #{order.id.slice(0, 8).toUpperCase()}
                                </p>
                                <p className="font-body text-xs text-stone-400">
                                  {orderDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                  {" "}
                                  {orderDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                                </p>
                              </div>
                              <div className="hidden sm:block">
                                <p className="font-body text-sm font-medium text-stone-700">{cust.name || "N/A"}</p>
                                <p className="font-body text-xs text-stone-400">{cust.email || ""}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={`rounded-full font-body text-xs ${
                                order.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"
                              }`}>
                                {order.status || "pending"}
                              </Badge>
                              <span className="font-heading text-lg font-bold text-[#78350F]">
                                ${(order.subtotal || 0).toFixed(2)}
                              </span>
                              {isExpanded ? <ChevronUp className="h-4 w-4 text-stone-400" /> : <ChevronDown className="h-4 w-4 text-stone-400" />}
                            </div>
                          </button>

                          {/* Expanded Details */}
                          {isExpanded && (
                            <div className="border-t border-stone-100 px-4 py-4 bg-stone-50/50">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div>
                                  <h4 className="font-body text-xs text-stone-400 uppercase tracking-wider mb-2">Customer Details</h4>
                                  <div className="space-y-1">
                                    <p className="font-body text-sm"><span className="text-stone-400 w-16 inline-block">Name:</span> <span className="text-stone-700 font-medium">{cust.name || "N/A"}</span></p>
                                    <p className="font-body text-sm"><span className="text-stone-400 w-16 inline-block">Email:</span> <span className="text-stone-700">{cust.email || "N/A"}</span></p>
                                    <p className="font-body text-sm"><span className="text-stone-400 w-16 inline-block">Phone:</span> <span className="text-stone-700">{cust.phone || "N/A"}</span></p>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="font-body text-xs text-stone-400 uppercase tracking-wider mb-2">Event Details</h4>
                                  <div className="space-y-1">
                                    <p className="font-body text-sm"><span className="text-stone-400 w-16 inline-block">Event:</span> <span className="text-stone-700 font-medium">{cust.event_type || "N/A"}</span></p>
                                    <p className="font-body text-sm"><span className="text-stone-400 w-16 inline-block">Date:</span> <span className="text-stone-700">{cust.event_date || "N/A"}</span></p>
                                    <p className="font-body text-sm"><span className="text-stone-400 w-16 inline-block">Address:</span> <span className="text-stone-700">{cust.delivery_address || "N/A"}</span></p>
                                    <p className="font-body text-sm"><span className="text-stone-400 w-16 inline-block">Time:</span> <span className="text-stone-700">{cust.delivery_time || "N/A"}</span></p>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <h4 className="font-body text-xs text-stone-400 uppercase tracking-wider mb-2">Items Ordered</h4>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="bg-stone-100/50">
                                      <TableHead className="font-body text-xs text-stone-500 uppercase">Item</TableHead>
                                      <TableHead className="font-body text-xs text-stone-500 uppercase text-center">Size</TableHead>
                                      <TableHead className="font-body text-xs text-stone-500 uppercase text-center">Qty</TableHead>
                                      <TableHead className="font-body text-xs text-stone-500 uppercase text-right">Price</TableHead>
                                      <TableHead className="font-body text-xs text-stone-500 uppercase text-right">Total</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {(order.items || []).map((item, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="font-body text-sm text-stone-700">{item.name}</TableCell>
                                        <TableCell className="font-body text-sm text-stone-500 text-center">
                                          {item.size === "per_piece" ? "Per Pc" : (item.size || "").replace("med_large", "Med-Lg").replace(/^\w/, c => c.toUpperCase())}
                                        </TableCell>
                                        <TableCell className="font-body text-sm text-stone-700 text-center">{item.quantity}</TableCell>
                                        <TableCell className="font-body text-sm text-stone-500 text-right">${Number(item.price || 0).toFixed(2)}</TableCell>
                                        <TableCell className="font-body text-sm font-medium text-[#78350F] text-right">${(item.price * item.quantity).toFixed(2)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                              {/* Order Actions */}
                              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-stone-200">
                                <span className="font-body text-xs text-stone-400 uppercase tracking-wider mr-2">Mark as:</span>
                                {order.status !== "completed" && (
                                  <Button
                                    data-testid={`order-complete-${order.id}`}
                                    size="sm"
                                    onClick={() => handleUpdateOrderStatus(order.id, "completed")}
                                    className="rounded-full bg-green-600 hover:bg-green-700 text-white font-body text-xs h-8 px-4"
                                  >
                                    <Check className="h-3.5 w-3.5 mr-1" /> Completed
                                  </Button>
                                )}
                                {order.status !== "cancelled" && (
                                  <Button
                                    data-testid={`order-cancel-${order.id}`}
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUpdateOrderStatus(order.id, "cancelled")}
                                    className="rounded-full border-amber-300 text-amber-600 hover:bg-amber-50 font-body text-xs h-8 px-4"
                                  >
                                    <X className="h-3.5 w-3.5 mr-1" /> Cancelled
                                  </Button>
                                )}
                                {order.status !== "pending" && (
                                  <Button
                                    data-testid={`order-pending-${order.id}`}
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUpdateOrderStatus(order.id, "pending")}
                                    className="rounded-full border-stone-300 text-stone-500 hover:bg-stone-50 font-body text-xs h-8 px-4"
                                  >
                                    Pending
                                  </Button>
                                )}
                                <div className="ml-auto">
                                  <Button
                                    data-testid={`order-delete-${order.id}`}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => { setDeleteOrderItem(order); setDeleteOrderOpen(true); }}
                                    className="rounded-full text-red-400 hover:text-red-600 hover:bg-red-50 font-body text-xs h-8 px-4"
                                  >
                                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Order
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </>
        )}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg bg-[#FFFBEB]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#78350F]">
              {dialogMode === "add" ? "Add Menu Item" : "Edit Menu Item"}
            </DialogTitle>
            <DialogDescription className="font-body text-stone-500">
              {dialogMode === "add"
                ? "Fill in the details for the new item"
                : "Update the item details below"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-sm text-stone-600">
                  Item Name
                </Label>
                <Input
                  data-testid="dialog-item-name"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, name: e.target.value }))
                  }
                  placeholder="e.g. Chicken Tikka"
                  className="bg-white border-stone-200 rounded-xl h-10 font-body"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm text-stone-600">
                  Category
                </Label>
                <Select
                  value={editForm.category}
                  onValueChange={(v) =>
                    setEditForm((p) => ({ ...p, category: v }))
                  }
                >
                  <SelectTrigger
                    data-testid="dialog-category"
                    className="bg-white border-stone-200 rounded-xl h-10 font-body"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem
                        key={c.key}
                        value={c.key}
                        className="font-body"
                      >
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-body text-sm text-stone-600">
                Description
              </Label>
              <Input
                data-testid="dialog-description"
                value={editForm.description}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, description: e.target.value }))
                }
                placeholder="Short description of the dish"
                className="bg-white border-stone-200 rounded-xl h-10 font-body"
              />
            </div>

            <div className="flex items-center gap-3">
              <Switch
                data-testid="dialog-is-veg"
                checked={editForm.is_veg}
                onCheckedChange={(v) =>
                  setEditForm((p) => ({ ...p, is_veg: v }))
                }
              />
              <Label className="font-body text-sm text-stone-600">
                {editForm.is_veg ? (
                  <span className="text-green-600 font-medium">
                    Vegetarian
                  </span>
                ) : (
                  <span className="text-red-600 font-medium">Non-Vegetarian</span>
                )}
              </Label>
            </div>

            <div className="flex items-center gap-3">
              <Label className="font-body text-sm text-stone-600">Pricing:</Label>
              <div className="flex items-center gap-1.5 bg-stone-50 rounded-full p-0.5">
                <button
                  type="button"
                  data-testid="dialog-pricing-tray"
                  onClick={() => setEditForm((p) => ({ ...p, pricing_type: "tray" }))}
                  className={`px-3 py-1 rounded-full text-xs font-body font-medium transition-all ${
                    editForm.pricing_type !== "per_piece"
                      ? "bg-[#EA580C] text-white"
                      : "text-stone-500"
                  }`}
                >
                  Per Tray
                </button>
                <button
                  type="button"
                  data-testid="dialog-pricing-piece"
                  onClick={() => setEditForm((p) => ({ ...p, pricing_type: "per_piece" }))}
                  className={`px-3 py-1 rounded-full text-xs font-body font-medium transition-all ${
                    editForm.pricing_type === "per_piece"
                      ? "bg-[#EA580C] text-white"
                      : "text-stone-500"
                  }`}
                >
                  Per Piece
                </button>
              </div>
            </div>

            <div>
              <Label className="font-body text-sm text-stone-600 mb-2 block">
                {editForm.pricing_type === "per_piece" ? "Price Per Piece ($)" : "Tray Prices ($)"}
              </Label>
              {editForm.pricing_type === "per_piece" ? (
                <div className="w-32">
                  <Input
                    data-testid="dialog-price-piece"
                    type="number"
                    min="0"
                    step="0.5"
                    value={editForm.prices.small}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      setEditForm((p) => ({
                        ...p,
                        prices: { small: val, medium: val, med_large: val, large: val },
                      }));
                    }}
                    className="bg-white border-stone-200 rounded-xl h-10 font-body text-center"
                  />
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  <div className="space-y-1">
                    <span className="font-body text-xs text-stone-400">Small</span>
                    <Input
                      data-testid="dialog-price-small"
                      type="number"
                      min="0"
                      value={editForm.prices.small}
                      onChange={(e) =>
                        setEditForm((p) => ({
                          ...p,
                          prices: { ...p.prices, small: Number(e.target.value) },
                        }))
                      }
                      className="bg-white border-stone-200 rounded-xl h-10 font-body text-center"
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="font-body text-xs text-stone-400">Medium</span>
                    <Input
                      data-testid="dialog-price-medium"
                      type="number"
                      min="0"
                      value={editForm.prices.medium}
                      onChange={(e) =>
                        setEditForm((p) => ({
                          ...p,
                          prices: { ...p.prices, medium: Number(e.target.value) },
                        }))
                      }
                      className="bg-white border-stone-200 rounded-xl h-10 font-body text-center"
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="font-body text-xs text-stone-400">Med-Lg</span>
                    <Input
                      data-testid="dialog-price-med-large"
                      type="number"
                      min="0"
                      value={editForm.prices.med_large}
                      onChange={(e) =>
                        setEditForm((p) => ({
                          ...p,
                          prices: { ...p.prices, med_large: Number(e.target.value) },
                        }))
                      }
                      className="bg-white border-stone-200 rounded-xl h-10 font-body text-center"
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="font-body text-xs text-stone-400">Large</span>
                    <Input
                      data-testid="dialog-price-large"
                      type="number"
                      min="0"
                      value={editForm.prices.large}
                      onChange={(e) =>
                        setEditForm((p) => ({
                          ...p,
                          prices: { ...p.prices, large: Number(e.target.value) },
                        }))
                      }
                      className="bg-white border-stone-200 rounded-xl h-10 font-body text-center"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              data-testid="dialog-cancel-btn"
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-full font-body"
            >
              Cancel
            </Button>
            <Button
              data-testid="dialog-save-btn"
              onClick={handleSave}
              disabled={saving || !editForm.name}
              className="rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold px-6 transition-all active:scale-95"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {dialogMode === "add" ? "Add Item" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-sm bg-[#FFFBEB]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#78350F]">
              Delete Item
            </DialogTitle>
            <DialogDescription className="font-body text-stone-500">
              Are you sure you want to remove{" "}
              <strong className="text-[#78350F]">{deleteItem?.name}</strong>{" "}
              from the menu? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              data-testid="delete-cancel-btn"
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              className="rounded-full font-body"
            >
              Cancel
            </Button>
            <Button
              data-testid="delete-confirm-btn"
              onClick={handleDelete}
              disabled={deleting}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white font-body font-semibold px-6"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Respond to Review Dialog */}
      <Dialog open={respondOpen} onOpenChange={setRespondOpen}>
        <DialogContent className="sm:max-w-md bg-[#FFFBEB]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#78350F]">
              {respondReview?.admin_response ? "Edit Response" : "Respond to Review"}
            </DialogTitle>
            <DialogDescription className="font-body text-stone-500">
              Reply to <strong className="text-[#78350F]">{respondReview?.name}</strong>'s review
            </DialogDescription>
          </DialogHeader>
          {respondReview && (
            <div className="bg-stone-50 rounded-xl p-3 my-2">
              <div className="flex items-center gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className={`h-3 w-3 ${s <= respondReview.rating ? "fill-amber-400 text-amber-400" : "fill-none text-stone-300"}`} />
                ))}
              </div>
              <p className="font-body text-sm text-stone-600 italic">"{respondReview.comment}"</p>
            </div>
          )}
          <div className="space-y-2">
            <Label className="font-body text-sm text-stone-600">Your Response</Label>
            <textarea
              data-testid="respond-text-input"
              value={respondText}
              onChange={(e) => setRespondText(e.target.value)}
              rows={3}
              placeholder="Write your response..."
              className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm font-body focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/20 resize-none"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondOpen(false)} className="rounded-full font-body">
              Cancel
            </Button>
            <Button
              data-testid="respond-submit-btn"
              onClick={handleRespond}
              disabled={responding || !respondText.trim()}
              className="rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold px-6"
            >
              {responding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageCircle className="h-4 w-4 mr-2" />}
              Post Response
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Review Confirmation */}
      <Dialog open={deleteReviewOpen} onOpenChange={setDeleteReviewOpen}>
        <DialogContent className="sm:max-w-sm bg-[#FFFBEB]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#78350F]">Delete Review</DialogTitle>
            <DialogDescription className="font-body text-stone-500">
              Are you sure you want to delete the review from <strong className="text-[#78350F]">{deleteReviewItem?.name}</strong>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteReviewOpen(false)} className="rounded-full font-body">
              Cancel
            </Button>
            <Button
              data-testid="delete-review-confirm-btn"
              onClick={handleDeleteReview}
              disabled={deletingReview}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white font-body font-semibold px-6"
            >
              {deletingReview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Order Confirmation Dialog */}
      <Dialog open={deleteOrderOpen} onOpenChange={setDeleteOrderOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#78350F]">Delete Order</DialogTitle>
            <DialogDescription className="font-body text-stone-600">
              Are you sure you want to delete order <strong className="text-[#78350F]">#{deleteOrderItem?.id?.slice(0, 8).toUpperCase()}</strong> from <strong>{deleteOrderItem?.customer?.name}</strong>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOrderOpen(false)} className="rounded-full font-body">
              Cancel
            </Button>
            <Button
              data-testid="delete-order-confirm-btn"
              onClick={handleDeleteOrder}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white font-body font-semibold px-6"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePassOpen} onOpenChange={(open) => { setChangePassOpen(open); if (!open) setPassForm({ current: "", new: "", confirm: "" }); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#78350F]">Change Password</DialogTitle>
            <DialogDescription className="font-body text-stone-600">
              Enter your current password and choose a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-body text-sm text-stone-600">Current Password</Label>
              <Input
                data-testid="change-pass-current"
                type="password"
                value={passForm.current}
                onChange={(e) => setPassForm(prev => ({ ...prev, current: e.target.value }))}
                className="mt-1 rounded-xl border-stone-200 font-body"
                placeholder="Enter current password"
              />
            </div>
            <div>
              <Label className="font-body text-sm text-stone-600">New Password</Label>
              <Input
                data-testid="change-pass-new"
                type="password"
                value={passForm.new}
                onChange={(e) => setPassForm(prev => ({ ...prev, new: e.target.value }))}
                className="mt-1 rounded-xl border-stone-200 font-body"
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <Label className="font-body text-sm text-stone-600">Confirm New Password</Label>
              <Input
                data-testid="change-pass-confirm"
                type="password"
                value={passForm.confirm}
                onChange={(e) => setPassForm(prev => ({ ...prev, confirm: e.target.value }))}
                className="mt-1 rounded-xl border-stone-200 font-body"
                placeholder="Re-enter new password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePassOpen(false)} className="rounded-full font-body">
              Cancel
            </Button>
            <Button
              data-testid="change-pass-submit"
              onClick={handleChangePassword}
              disabled={passLoading || !passForm.current || !passForm.new || !passForm.confirm}
              className="rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold px-6"
            >
              {passLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
